/**
 * The reading preferences a reader can set, and the one file they export.
 *
 * Kept out of the screen component so Fast Refresh keeps working and so the
 * reader and the settings screen agree on the storage keys rather than each
 * spelling them out.
 *
 * **Every one of these is browser-local, and that is the honest state of the
 * product, not a shortcut.** There are no accounts (brief §18 defers
 * authentication deliberately), so there is nowhere else for a preference to
 * live. The mock's answer is the one taken here: say so where the data is,
 * and offer an export rather than designing a sign-in that would decide the
 * authentication question by accident.
 */

import { isLeading, isStep, type Leading, type ReaderStep } from '@/features/reader/readerSettings';
import { isReadingWidth, type ReadingWidth } from '@/features/reader/readerSettings';
import { isTheme, type Theme } from '@/lib/theme';

/** The reader's default language, used when a link does not name one. */
export const READING_LANGUAGE_KEY = 'mimbar.reader.language';
export const STEP_KEY = 'mimbar.reader.step';
export const LEADING_KEY = 'mimbar.reader.leading';
export const WIDTH_KEY = 'mimbar.reader.width';
export const VIEW_KEY = 'mimbar.reader.view';
export const CHAT_LANGUAGE_KEY = 'mimbar.chat.language';
export const THEME_KEY = 'mimbar-theme';

/**
 * Chat follows the reading language unless the reader says otherwise.
 *
 * Stored as this literal rather than as a resolved code, so that changing the
 * reading language carries the chat with it — which is what "follows" has to
 * mean to be worth having.
 */
export const CHAT_FOLLOWS_READING = 'same-as-reading';

export interface ExportedPreferences {
  readonly kind: 'mimbar.preferences';
  readonly version: 1;
  readonly exportedAt: string;
  readonly values: Record<string, string>;
}

/** Every key this product is allowed to export or import. */
const EXPORTABLE = [
  READING_LANGUAGE_KEY,
  STEP_KEY,
  LEADING_KEY,
  WIDTH_KEY,
  VIEW_KEY,
  CHAT_LANGUAGE_KEY,
  THEME_KEY,
] as const;

/**
 * What each key will accept back.
 *
 * An import reads a file the reader supplies, so it is untrusted input and
 * every value is validated before it is stored — the same rule the API
 * boundary follows. Without this, a hand-edited file could put `theme:
 * "<script>"` into storage and the provider would hand it straight to a class
 * name.
 */
const VALID: Record<string, (value: string) => boolean> = {
  [READING_LANGUAGE_KEY]: (v) => /^[a-z]{2,3}$/.test(v),
  [STEP_KEY]: (v) => isStep(v),
  [LEADING_KEY]: (v) => isLeading(v),
  [WIDTH_KEY]: (v) => isReadingWidth(v),
  [VIEW_KEY]: (v) => ['side-by-side', 'stacked', 'translation', 'arabic'].includes(v),
  [CHAT_LANGUAGE_KEY]: (v) => v === CHAT_FOLLOWS_READING || /^[a-z]{2,3}$/.test(v),
  [THEME_KEY]: (v) => isTheme(v),
};

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function exportPreferences(now: Date): ExportedPreferences {
  const values: Record<string, string> = {};
  for (const key of EXPORTABLE) {
    const value = read(key);
    if (value !== null) {
      values[key] = value;
    }
  }
  return {
    kind: 'mimbar.preferences',
    version: 1,
    exportedAt: now.toISOString(),
    values,
  };
}

export interface ImportOutcome {
  readonly applied: number;
  /** Keys the file carried that this version will not accept, named. */
  readonly rejected: readonly string[];
}

/**
 * Applies an exported file, rejecting anything it does not recognise.
 *
 * Returns what it did rather than throwing on a partial file: a file written
 * by a newer version may carry keys this one has never heard of, and the
 * right answer is to take what is understood and say what was left.
 */
export function importPreferences(parsed: unknown): ImportOutcome {
  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    (parsed as { kind?: unknown }).kind !== 'mimbar.preferences'
  ) {
    throw new Error('That is not a Mimbar preferences file.');
  }
  const values = (parsed as { values?: unknown }).values;
  if (typeof values !== 'object' || values === null) {
    throw new Error('The file has no preferences in it.');
  }

  let applied = 0;
  const rejected: string[] = [];
  for (const [key, value] of Object.entries(values as Record<string, unknown>)) {
    const check = VALID[key];
    if (typeof value !== 'string' || check === undefined || !check(value)) {
      rejected.push(key);
      continue;
    }
    try {
      localStorage.setItem(key, value);
      applied += 1;
    } catch {
      rejected.push(key);
    }
  }
  return { applied, rejected };
}

/** Everything this product has put in this browser, for the clear action. */
export function clearPreferences(): void {
  for (const key of EXPORTABLE) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage can throw outright; a preference that will not clear is not
      // worth failing the whole action over.
    }
  }
}

export type { Leading, ReaderStep, ReadingWidth, Theme };
